import React from 'react';
import parse from 'html-react-parser';
import CtaButton from '../../ctaButton/ctaButton';
import styles from './heroHomeTemplate.module.scss';
import TrustPilotStar from './images/TrustPilotFrame.svg';
import FDICShield from './images/FDICFrame.svg';
import RespondToEmail from './images/RespondToEmail.svg';
import RespondToEmailWhite from './images/RespondToEmailWhite.svg';
import HeroCarousel from '../heroCarousel/heroCarousel';
import HeroMobileList from '../heroMobileList/heroMobileList';
import trackHeapEvent from '../../../services/helpers/trackHeapEvent';

function HeroHomeTemplateEggshell({
  header = '',
  title = '',
  heroDesc = '',
  heroSubDesc = '',
  ctaWrapper = [],
  trustPilotRating = '4.8',
  disclaimerText = '',
  prequalItems = {},
}) {
  const carouselItems =
    prequalItems?.loanPurposes
      ?.filter((item) => item.headline)
      .map((item) => ({
        ...item,
        ctaButton: item.ctaButton
          ? {
              link: item.ctaButton.link,
              heapKey: item.ctaButton.heapEventKey,
              label: item.ctaButton.title,
            }
          : null,
      })) || [];

  const heroEmailTrack = () => {
    const heapEventKey = prequalItems?.contentEmail?.heapEventKey;

    trackHeapEvent(
      {
        Name: 'Home Page Hero - Respond to Mail Offer',
        CTA: heapEventKey,
      },
      'CMS_Unification'
    );

    window.location.assign(prequalItems?.contentEmail?.link);
  };

  const handleCTAClick = (cta, index) => {
    const ctaName = index === 0 ? 'Primary CTA' : 'Secondary CTA';
    const { heapEventKey } = cta;

    trackHeapEvent(
      {
        Name: `Home Page Hero - ${ctaName}`,
        CTA: heapEventKey,
      },
      'Hero-CTA-buttons'
    );
    window.location.assign(cta?.link);
  };

  const handleCardCTAClick = (card) => {
    const params = new URLSearchParams(window.location.search);
    const { experimentName, experimentVariation } = prequalItems;

    const loanPurpose = card.loanLabel;
    if (loanPurpose) {
      params.set('loanPurpose', loanPurpose);
    }

    if (experimentName && experimentVariation) {
      const capitalizeFirstLetter = (str) =>
        str.charAt(0).toUpperCase() + str.slice(1);
      params.set(
        'experiment',
        `${experimentName}__${capitalizeFirstLetter(experimentVariation)}`
      );
    }

    const cardUrl = card.ctaButton?.link;
    const queryString = params.toString();
    const newURL = queryString ? `${cardUrl}?${queryString}` : cardUrl;

    trackHeapEvent(
      {
        'Card Name': card.headline,
        CTA: card.ctaButton?.heapKey,
      },
      'Hero-Card-Click'
    );

    window.location.assign(newURL);
  };

  return (
    <section className={styles.heroEggshellContainer}>
      <div className={styles.content}>
        <div className={styles.heroHeader}>
          <h1 className={styles.headline}>
            {header && (
              <div className={styles.headlinePart}>{parse(header)}</div>
            )}
            {title && <div className={styles.headlinePart}>{parse(title)}</div>}
          </h1>

          <div className={styles.subheadGroup}>
            {heroDesc && (
              <div className={styles.description}>{parse(heroDesc)}</div>
            )}
            {heroSubDesc && (
              <div className={styles.subDescription}>{parse(heroSubDesc)}</div>
            )}
          </div>
        </div>

        <div className={styles.actionArea}>
          <div
            className={styles.trustPilot}
            role="img"
            aria-label={`Trustpilot rating ${trustPilotRating} out of 5`}
          >
            <img
              src={TrustPilotStar.src || TrustPilotStar}
              alt=""
              className={styles.starIcon}
            />
            {trustPilotRating} Trustpilot
          </div>

          <div className={styles.ctaContainer}>
            <div className={styles.buttonRow}>
              {ctaWrapper &&
                ctaWrapper.map((cta, index) => (
                  <CtaButton
                    key={cta.link || index}
                    label={cta.title}
                    href={cta.link}
                    variant={cta.variant}
                    onClick={() => handleCTAClick(cta, index)}
                  />
                ))}
            </div>
            {disclaimerText && (
              <div className={styles.disclaimer}>{disclaimerText}</div>
            )}
          </div>

          <div
            className={styles.fdicBadge}
            role="img"
            aria-label="FDIC Insured"
          >
            <img
              src={FDICShield.src || FDICShield}
              alt=""
              className={styles.badgeIcon}
            />
            FDIC Insured
          </div>
        </div>

        {prequalItems?.contentEmail && (
          <div
            className={styles.respondToMailOfferMobile}
            onClick={heroEmailTrack}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                heroEmailTrack();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Respond to Mail Offer"
          >
            <img
              src={RespondToEmail.src || RespondToEmail}
              alt=""
              className={styles.mailOfferIcon}
            />
            <span className={styles.mailOfferText}>Respond to Mail Offer</span>
          </div>
        )}

        {carouselItems && carouselItems.length > 0 && (
          <>
            <div className={styles.desktopCarousel}>
              <HeroCarousel
                items={carouselItems}
                onCardClick={handleCardCTAClick}
                mailOfferContent={
                  prequalItems?.contentEmail && (
                    <div
                      className={styles.respondToMailOfferCard}
                      onClick={heroEmailTrack}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          heroEmailTrack();
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label="Respond to Mail Offer"
                    >
                      <img
                        src={RespondToEmailWhite.src || RespondToEmailWhite}
                        alt=""
                        className={styles.mailOfferIconCard}
                      />
                      <span className={styles.mailOfferTextCard}>
                        Respond to Mail Offer
                      </span>
                    </div>
                  )
                }
              />
            </div>

            <div className={styles.mobileList}>
              <HeroMobileList
                items={carouselItems}
                onCardClick={handleCardCTAClick}
              />
            </div>
          </>
        )}
        <div
          className={styles.respondToMailOffer}
          onClick={heroEmailTrack}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              heroEmailTrack();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Respond to Mail Offer"
        >
          <img
            src={RespondToEmail.src || RespondToEmail}
            alt=""
            className={styles.mailOfferIcon}
          />
          <span className={styles.mailOfferText}>Respond to Mail Offer</span>
        </div>
      </div>
    </section>
  );
}

export default HeroHomeTemplateEggshell;
